import React, { useState, useEffect } from 'react';

const DaycareFreezeModal = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  enrollment,
  apiCall,
  showError,
  showSuccess 
}) => {
  const [freezeReason, setFreezeReason] = useState('');
  const [freezeStartDate, setFreezeStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [freezeEndDate, setFreezeEndDate] = useState('');
  const [cancelCharges, setCancelCharges] = useState(true); // ✅ NEW
  const [loading, setLoading] = useState(false);
  
  // ✅ NEW: Calculate affected months
  const [affectedMonths, setAffectedMonths] = useState([]);
  
  useEffect(() => {
    if (freezeStartDate && freezeEndDate) {
      const start = new Date(freezeStartDate);
      const end = new Date(freezeEndDate);
      const months = [];
      
      let current = new Date(start.getFullYear(), start.getMonth(), 1);
      const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
      
      while (current <= endMonth) {
        months.push({
          month: current.getMonth() + 1,
          year: current.getFullYear(),
          name: current.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
        });
        current.setMonth(current.getMonth() + 1);
      }
      
      setAffectedMonths(months);
    } else if (freezeStartDate) {
      const start = new Date(freezeStartDate);
      setAffectedMonths([{
        month: start.getMonth() + 1,
        year: start.getFullYear(),
        name: start.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      }]);
    } else {
      setAffectedMonths([]);
    }
  }, [freezeStartDate, freezeEndDate]);

  const handleFreeze = async (e) => {
    e.preventDefault();
    
    if (!freezeReason.trim()) {
      showError('Please provide a reason for freezing');
      return;
    }

    setLoading(true);
    try {
      const response = await apiCall(`/daycare/enrollments/${enrollment.EnrollmentID}/freeze`, {
        method: 'PUT',
        body: JSON.stringify({
          freezeReason,
          freezeStartDate,
          freezeEndDate: freezeEndDate || null,
          cancelCharges // ✅ NEW: Send cancel preference
        })
      });

      // ✅ Show how many charges were cancelled
      if (response.chargesCancelled > 0) {
        showSuccess(
          `Enrollment frozen for ${enrollment.FirstName} ${enrollment.LastName}. ` +
          `${response.chargesCancelled} charge(s) cancelled (Total: $${response.totalCancelled})`
        );
      } else {
        showSuccess(`Enrollment frozen for ${enrollment.FirstName} ${enrollment.LastName}`);
      }
      
      onSuccess();
      handleClose();
    } catch (error) {
      showError('Error freezing enrollment: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFreezeReason('');
    setFreezeStartDate(new Date().toISOString().split('T')[0]);
    setFreezeEndDate('');
    setCancelCharges(true);
    setAffectedMonths([]);
    onClose();
  };

  if (!isOpen || !enrollment) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white">Freeze Enrollment</h2>
            <p className="text-blue-100 text-sm mt-1">
              Temporarily pause enrollment
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-white hover:text-gray-200 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Enrollment Info */}
        <div className="px-6 py-4 bg-blue-50 border-b border-blue-200">
          <div className="space-y-2 text-sm">
            <div>
              <span className="text-gray-600">Child:</span>
              <span className="ml-2 font-semibold text-gray-900">
                {enrollment.FirstName} {enrollment.LastName}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Package:</span>
              <span className="ml-2 font-semibold text-gray-900">
                {enrollment.PackageName} (${enrollment.FinalMonthlyRate}/month)
              </span>
            </div>
            <div>
              <span className="text-gray-600">Current Status:</span>
              <span className="ml-2 font-semibold text-green-600">
                Active
              </span>
            </div>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleFreeze} className="p-6 space-y-4">
          
          {/* Freeze Reason */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reason for Freezing <span className="text-red-500">*</span>
            </label>
            <textarea
              value={freezeReason}
              onChange={(e) => setFreezeReason(e.target.value)}
              placeholder="e.g., Family vacation, Medical leave, Temporary relocation..."
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Start Date */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Freeze Start Date
            </label>
            <input
              type="date"
              value={freezeStartDate}
              onChange={(e) => setFreezeStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          {/* Expected End Date (Optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Expected Return Date (Optional)
            </label>
            <input
              type="date"
              value={freezeEndDate}
              onChange={(e) => setFreezeEndDate(e.target.value)}
              min={freezeStartDate}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <p className="text-xs text-gray-500 mt-1">
              Leave empty if return date is uncertain
            </p>
          </div>

          {/* ✅ NEW: Cancel Charges Option */}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
            <div className="flex items-start">
              <input
                type="checkbox"
                id="cancelCharges"
                checked={cancelCharges}
                onChange={(e) => setCancelCharges(e.target.checked)}
                className="mt-1 mr-3 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <div className="flex-1">
                <label htmlFor="cancelCharges" className="block text-sm font-medium text-gray-900 cursor-pointer">
                  Cancel charges for frozen period
                </label>
                <p className="text-xs text-gray-600 mt-1">
                  If checked, any unpaid charges for the frozen months will be cancelled.
                  You can add a discounted charge when unfreezing.
                </p>
                
                {/* Show affected months */}
                {affectedMonths.length > 0 && (
                  <div className="mt-2 p-2 bg-white rounded border border-orange-300">
                    <p className="text-xs font-medium text-gray-700 mb-1">Affected months:</p>
                    <div className="flex flex-wrap gap-1">
                      {affectedMonths.map((m, idx) => (
                        <span key={idx} className="inline-block px-2 py-0.5 bg-orange-100 text-orange-800 text-xs rounded">
                          {m.name}
                        </span>
                      ))}
                    </div>
                    {cancelCharges && (
                      <p className="text-xs text-orange-700 mt-2">
                        ⚠️ Unpaid charges for these months will be cancelled
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
            <div className="flex items-start">
              <svg className="w-5 h-5 text-blue-600 mr-2 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div className="text-sm text-blue-800">
                <p className="font-medium">What happens when frozen:</p>
                <ul className="mt-1 space-y-1 list-disc list-inside text-xs">
                  <li>Status changes to "Frozen"</li>
                  <li>Spot remains reserved</li>
                  <li>Can be unfrozen at any time</li>
                  <li>No new charges while frozen</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Freezing...' : '🧊 Freeze Enrollment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DaycareFreezeModal;